import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";

export default function Saved() {
  const [saved, setSaved] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingNote, setEditingNote] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("saved_properties")
        .select("*")
        .order("created_at", { ascending: false });

      if (!error) setSaved(data || []);
      else console.error(error);
    })();
  }, []);

  async function deleteSaved(id) {
    if (!window.confirm("Delete this saved property?")) return;

    const { error } = await supabase.from("saved_properties").delete().eq("id", id);
    if (!error) setSaved((prev) => prev.filter((p) => p.id !== id));
    else console.error("Delete error:", error);
  }

  function openEditModal(id, note) {
    setEditingId(id);
    setEditingNote(note || "");
    setIsModalOpen(true);
  }

  async function handleUpdateNote() {
    const { error } = await supabase
      .from("saved_properties")
      .update({ notes: editingNote })
      .eq("id", editingId);

    if (!error) {
      setSaved((prev) =>
        prev.map((p) => (p.id === editingId ? { ...p, notes: editingNote } : p))
      );
      setIsModalOpen(false);
    } else {
      console.error("Update error:", error);
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-blue-700 flex items-center gap-2">
            💾 Saved Properties
          </h1>

          <button
            onClick={() => navigate("/app")}
            className="inline-flex items-center text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Back to Map
          </button>
        </div>

        {saved.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            You haven&apos;t saved any properties yet.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
            {saved.map((item) => (
              <div
                key={item.id}
                className="p-4 border border-gray-200 rounded-lg bg-white shadow hover:shadow-md transition space-y-1"
              >
                <h2 className="text-base font-semibold text-blue-800">
                  📍 {item.property_id}
                </h2>

                <p className="text-sm text-gray-700 break-words">
                  📝 <strong>Note:</strong>{" "}
                  {item.notes?.trim() ? item.notes : "—"}
                </p>

                <p className="text-xs text-gray-400">
                  Saved:&nbsp;
                  {item.created_at
                    ? new Date(item.created_at).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })
                    : "N/A"}
                </p>

                <div className="mt-3 flex justify-end gap-2">
                  <button
                    onClick={() => openEditModal(item.id, item.notes)}
                    className="flex items-center gap-1 text-sm text-white bg-blue-500 hover:bg-blue-600 px-2 py-1 rounded"
                  >
                    <Pencil size={14} /> Edit
                  </button>
                  <button
                    onClick={() => deleteSaved(item.id)}
                    className="flex items-center gap-1 text-sm text-white bg-red-500 hover:bg-red-600 px-2 py-1 rounded"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-3">Edit Note</h2>
            <textarea
              className="w-full border rounded p-2 text-sm"
              rows={4}
              value={editingNote}
              onChange={(e) => setEditingNote(e.target.value)}
              placeholder="Edit your note..."
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="bg-gray-200 hover:bg-gray-300 text-sm px-3 py-1 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateNote}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1 rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
